import { type Dayjs } from "dayjs"
import { arrayRemove, arrayUnion } from "firebase/firestore"
import { keyBy } from "lodash"

import { EXCEPTION_LIST_VIEWS } from "#constants/exceptionListConstants"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import {
  type AddClassFormSubmitValues,
  type AddClassFormValues,
} from "#customTypes/composedTypes/classTypes"
import { FirebaseBatchUpdateClass } from "#customTypes/composedTypes/firebaseTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"
import {
  Attendance,
  AttendanceClassSessions,
  AttendanceStatus,
  EnumValues,
  invariant,
  IsoDate,
} from "#customTypes/types"
import {
  ExceptionStudentData,
  StudentsByExceptionType,
} from "#hooks/newHooks/useExceptionListData"
import { hasRealEnrollmentHistory } from "#utils/studentHelpers"

import { setDayjsTimeToZero, STANDARD_DATE_FORMAT } from "./dateHelpers"
import { SKIP_DAYS } from "./hardcodedSettings"
import { getNewAttendanceForClass } from "./studentHelpers"

// No need to include plannedAbsenceIds because they are removed from studentIds already
export const getSessionHeadcount = (
  session: ClassSessionConstructor | undefined | null
): number => {
  if (!session) return 0

  const { studentIds = {}, tryoutIds = {}, makeupIds = {} } = session

  const studentsCount = Object.keys(studentIds).length
  const tryoutsCount = Object.keys(tryoutIds).length
  const makeupsCount = Object.keys(makeupIds).length

  return studentsCount + makeupsCount + tryoutsCount
}

export const generateSessionId = (
  classId: Uuid,
  sessionIndex: number
): string => {
  return `${classId}-${sessionIndex.toString().padStart(3, "0")}`
}

export const generateSessionIds = (classId: Uuid, numberOfSessions: number) => {
  return Array.from({ length: numberOfSessions }, (_, i) =>
    generateSessionId(classId, i)
  )
}

export const getSessionInfoFromId = (id: Uuid): [Uuid, number] => {
  const lastHyphenIndex = id.lastIndexOf("-")
  const classId = id.slice(0, lastHyphenIndex)
  const sessionIndex = parseInt(id.slice(lastHyphenIndex + 1))
  return [classId, sessionIndex]
}

export const calculateSessionDateByIndex = (
  startDate: Dayjs,
  sessionIndex: number,
  isSummerCamp: boolean
): Dayjs => {
  return startDate.add(sessionIndex, isSummerCamp ? "days" : "weeks")
}

export interface SessionDates {
  eventStart: IsoDate
  eventEnd: IsoDate
}

export const generateSessionDates = ({
  startDate,
  times,
  isSummerCamp,
  numberOfSessions,
}: {
  startDate: Dayjs
  times: [Dayjs, Dayjs]
  isSummerCamp: boolean
  numberOfSessions: number
}): SessionDates[] => {
  const [startTime, endTime] = times
  const eventStart = setDayjsTimeToZero(startDate)
    .set("hour", startTime.hour())
    .set("minute", startTime.minute())
  const eventEnd = setDayjsTimeToZero(startDate)
    .set("hour", endTime.hour())
    .set("minute", endTime.minute())

  const sessions: SessionDates[] = []

  let skippedCount = 0
  // Do not include students, makeups, tryouts, assistants, teachers here! They will overwrite!
  for (let i = 0; i < numberOfSessions; i++) {
    let sessionStartDate = calculateSessionDateByIndex(
      eventStart,
      i + skippedCount,
      isSummerCamp
    )

    // Recalculate if the session date is a skip day
    if (SKIP_DAYS.includes(sessionStartDate.format(STANDARD_DATE_FORMAT))) {
      skippedCount++

      sessionStartDate = calculateSessionDateByIndex(
        eventStart,
        i + skippedCount,
        isSummerCamp
      )
    }

    sessions.push({
      eventStart: sessionStartDate.toISOString(),
      eventEnd: calculateSessionDateByIndex(
        eventEnd,
        i + skippedCount,
        isSummerCamp
      ).toISOString(),
    })
  }

  return sessions
}

export const generateClassSessions = (
  values: AddClassFormValues,
  numberOfSessions: number
): AddClassFormSubmitValues["sessions"] => {
  const { startDate, times, isSummerCamp, id, semester } = values
  const [startTime, endTime] = times ?? []

  invariant(startDate != null, "startDate is null")
  invariant(startTime != null, "startTime is null")
  invariant(endTime != null, "endTime is null")

  const sessionDates = generateSessionDates({
    startDate,
    times: [startTime, endTime],
    isSummerCamp,
    numberOfSessions,
  })

  return sessionDates.map((session, i) => {
    return {
      ...session,
      id: id ? generateSessionId(id, i) : null,
      classId: id,
      semester,
    }
  })
}

export interface SessionStudentsByType {
  studentAttendanceStatuses: AttendanceStatus[]
  makeupAttendanceStatuses: AttendanceStatus[]
  tryoutAttendanceStatuses: AttendanceStatus[]
}
export interface AttendanceSummary {
  PRESENT: number
  TARDY: number
  ABSENT: number
  ENROLLED: number
  TOTAL: number
}

export const summarizeSessionStudentsByType = (
  session: ClassSessionConstructor,
  studentIds: Uuid[] = [],
  exceptionListData: StudentsByExceptionType
): {
  students: number
  makeups: number
  tryouts: number
  plannedAbsences: number
} => {
  const { plannedAbsences = [], makeups = [], tryouts = [] } = exceptionListData
  const sessionMakeups: ExceptionStudentData[] =
    makeups.filter(({ sessionId }) => sessionId === session.id) ?? []
  const sessionTryouts: ExceptionStudentData[] =
    tryouts.filter(({ sessionId }) => sessionId === session.id) ?? []
  const sessionPlannedAbsences: ExceptionStudentData[] =
    plannedAbsences.filter(({ sessionId }) => sessionId === session.id) ?? []

  return {
    students: studentIds.length,
    makeups: sessionMakeups.length,
    tryouts: sessionTryouts.length,
    plannedAbsences: sessionPlannedAbsences.length,
  }
}

const summarizeSessionAttendance = (
  sessionStudentsByType: SessionStudentsByType
): AttendanceSummary => {
  const {
    studentAttendanceStatuses,
    makeupAttendanceStatuses,
    tryoutAttendanceStatuses,
  } = sessionStudentsByType
  // Initialize counters
  const summary: AttendanceSummary = {
    PRESENT: 0,
    TARDY: 0,
    ABSENT: 0,
    ENROLLED: 0,
    TOTAL: 0,
  }

  // Combine all student records into one array
  const allAttendanceStatuses = [
    ...studentAttendanceStatuses,
    ...makeupAttendanceStatuses,
    ...tryoutAttendanceStatuses,
  ]

  // Count each attendance status
  allAttendanceStatuses.forEach((status) => {
    const typesafeStatus = status as AttendanceStatus

    if (typesafeStatus.includes("PRESENT")) {
      summary.PRESENT++
    } else if (typesafeStatus.includes("TARDY")) {
      summary.TARDY++
    } else if (typesafeStatus.includes("ABSENT")) {
      summary.ABSENT++
    } else if (typesafeStatus.includes("ENROLLED")) {
      summary.ENROLLED++
    }
  })

  summary.TOTAL =
    summary.PRESENT + summary.TARDY + summary.ABSENT + summary.ENROLLED
  return summary
}

export const isDefined = <T>(item: T | undefined): item is T => {
  return item !== undefined
}

export const getSessionAttendanceSummary = (
  session: ClassSessionConstructor,
  studentsById: Record<Uuid, StudentConstructor>,
  exceptionListData: StudentsByExceptionType
): AttendanceSummary => {
  if (!exceptionListData) {
    return {
      PRESENT: 0,
      TARDY: 0,
      ABSENT: 0,
      ENROLLED: 0,
      TOTAL: 0,
    }
  }
  const [classId, sessionIndex] = getSessionInfoFromId(session.id)

  const studentAttendanceStatuses: AttendanceStatus[] =
    session.studentIds
      ?.map(
        (id) => studentsById[id]?.attendance?.[classId]?.[sessionIndex]?.status
      )
      .filter(isDefined) ?? []
  const makeupAttendanceStatuses: AttendanceStatus[] =
    exceptionListData[EXCEPTION_LIST_VIEWS.makeups]
      .filter(({ sessionId }) => sessionId === session.id)
      .map(
        ({ student }) => student.attendance?.[classId]?.[sessionIndex]?.status
      )
      .filter(isDefined) ?? []
  const tryoutAttendanceStatuses: AttendanceStatus[] =
    exceptionListData[EXCEPTION_LIST_VIEWS.tryouts]
      .filter(({ sessionId }) => sessionId === session.id)
      .map(
        ({ student }) => student.attendance?.[classId]?.[sessionIndex]?.status
      )
      .filter(isDefined) ?? []

  return summarizeSessionAttendance({
    studentAttendanceStatuses,
    makeupAttendanceStatuses,
    tryoutAttendanceStatuses,
  })
}

export const isAttendanceGreen = (attendance: AttendanceSummary): boolean => {
  const { ENROLLED } = attendance
  return !ENROLLED
}

export interface Exception {
  avatar: string
  dob: string
  name: string
  attendance: EnumValues<typeof ATTENDANCE_STATUSES>
  note: string
  room: string
}

export interface AllSessionMemberIds {
  assistantIds: Uuid[]
  makeupIds: Uuid[]
  tryoutIds: Uuid[]
  studentIds: Uuid[]
  teacherIds: Uuid[]
  plannedAbsenceIds: Uuid[]
}

export const getAllSessionMemberIds = (
  sessions: ClassSessionConstructor[]
): AllSessionMemberIds => {
  if (!sessions?.length) {
    return {
      assistantIds: [],
      makeupIds: [],
      tryoutIds: [],
      studentIds: [],
      teacherIds: [],
      plannedAbsenceIds: [],
    }
  }

  // TODO: Figure out a better way to do this
  return {
    assistantIds: [
      ...new Set(sessions.flatMap((session) => session.assistantIds ?? [])),
    ].filter(Boolean),
    makeupIds: [
      ...new Set(sessions.flatMap((session) => session.makeupIds ?? [])),
    ].filter(Boolean),
    tryoutIds: [
      ...new Set(sessions.flatMap((session) => session.tryoutIds ?? [])),
    ].filter(Boolean),
    studentIds: [
      ...new Set(sessions.flatMap((session) => session.studentIds ?? [])),
    ].filter(Boolean),
    teacherIds: [
      ...new Set(sessions.flatMap((session) => session.teacherIds ?? [])),
    ].filter(Boolean),
    plannedAbsenceIds: [
      ...new Set(
        sessions.flatMap((session) => session.plannedAbsenceIds ?? [])
      ),
    ].filter(Boolean),
  }
}

const PERSON_ATTENDANCE_FIELDS_BY_TYPE = {
  student: "attendance",
  teacher: "teacherAttendance",
  assistant: "assistantAttendance",
}

const CLASS_MEMBER_ID_FIELDS_BY_TYPE = {
  student: "studentIds",
  teacher: "teacherIds",
  assistant: "assistantIds",
}

// We only change attendance if the status is blank
const MUTABLE_STATUSES: AttendanceStatus[] = [
  ATTENDANCE_STATUSES.ENROLLED,
  ATTENDANCE_STATUSES.NOT_ENROLLED,
  ATTENDANCE_STATUSES.INVALID,
] as const

export const isAttendanceStatusMutable = (status: AttendanceStatus) => {
  return MUTABLE_STATUSES.includes(status)
}

/**
 * Builds the complete set of batch updates needed to enroll/unenroll
 * a person in a class based on selected session indexes.
 *
 * Handles:
 * - Computing new attendance for the class via getNewAttendanceForClass
 * - Mutating the working attendance record (deleting the class key when fully unenrolled)
 * - Building class and session batch updates via buildAttendanceUpdate
 *
 * @param selectedSessionIndexes - boolean[] of which sessions are selected
 * @param classSessions - the class session docs (aligned by index)
 * @param attendanceForClass - the current display attendance for the class
 * @param classId - the class being enrolled/unenrolled
 * @param personId - the student/teacher/assistant ID
 * @param workingAttendance - mutable full attendance record; class key is deleted when unenrolled
 * @param attendanceType - "student" | "teacher" | "assistant"
 */
export const buildClassEnrollmentUpdate = (
  selectedSessionIndexes: boolean[],
  classSessions: { id: Uuid }[],
  attendanceForClass: AttendanceClassSessions,
  classId: Uuid,
  personId: Uuid,
  workingAttendance: Attendance,
  attendanceType: "student" | "teacher" | "assistant" = "student"
) => {
  const selectedSessionsWithIds = selectedSessionIndexes.map(
    (isSelected, i) => ({
      isSelected,
      sessionId: classSessions[i].id,
    })
  )

  const newAttendanceForClass = getNewAttendanceForClass(
    attendanceForClass,
    selectedSessionsWithIds
  )

  // Mutate working attendance directly so callers don't need to handle merge
  if (newAttendanceForClass) {
    workingAttendance[classId] = newAttendanceForClass
  } else {
    delete workingAttendance[classId]
  }

  const {
    person,
    class: classData,
    classSessionsById,
  } = buildAttendanceUpdate(
    newAttendanceForClass,
    selectedSessionsWithIds,
    classId,
    personId,
    attendanceType,
    workingAttendance
  )

  return {
    person,
    class: classData,
    classSessionsById,
    newAttendanceForClass,
    isStillEnrolled: newAttendanceForClass != null,
  }
}

export const buildAttendanceUpdate = (
  newAttendance: AttendanceClassSessions | null,
  selectedSessions: { isSelected: boolean; sessionId: Uuid }[],
  classId: Uuid,
  personId: Uuid,
  attendanceType: "student" | "teacher" | "assistant",
  // Firestore docs can store an explicit null for the attendance field, which
  // bypasses the default value (defaults only apply to undefined)
  oldAttendance?: Attendance | null
) => {
  const existingAttendance = oldAttendance ?? {}
  // getNewAttendanceForClass returns null if no attendance remains at all
  const hasAttendance = newAttendance != null
  // class.studentIds / session.studentIds should only include real enrollment,
  // not tryout/makeup-only attendance
  const hasRealEnrollment =
    hasAttendance && hasRealEnrollmentHistory(newAttendance)

  const attendanceField = PERSON_ATTENDANCE_FIELDS_BY_TYPE[attendanceType]
  const idField = CLASS_MEMBER_ID_FIELDS_BY_TYPE[attendanceType]

  const newPersonData = {
    [attendanceField]: {
      ...existingAttendance,
    },
  }

  if (hasAttendance) {
    newPersonData[attendanceField][classId] = newAttendance
  } else {
    delete newPersonData[attendanceField][classId]
  }

  const newClassData: FirebaseBatchUpdateClass = {
    [idField]: hasRealEnrollment ? arrayUnion(personId) : arrayRemove(personId),
  }

  const oldAttendanceForClass = existingAttendance[classId]

  const newClassSessionData = selectedSessions.map(
    ({ isSelected, sessionId }, i) => {
      const existingStatus =
        oldAttendanceForClass?.[i]?.status ?? ATTENDANCE_STATUSES.NOT_ENROLLED
      const isValid = isAttendanceStatusMutable(existingStatus)

      // Don't modify session list if status has already been recorded
      if (!isValid) {
        return {
          id: sessionId,
        }
      }

      return {
        id: sessionId,
        [idField]: isSelected ? arrayUnion(personId) : arrayRemove(personId),
      }
    }
  )
  const sessionDataById = keyBy(newClassSessionData, "id")

  return {
    person: newPersonData,
    class: newClassData,
    classSessionsById: sessionDataById,
  }
}
